import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { useStatsStore } from '../store';

import { useStatisticTypes } from './statTypes';

import { Order } from '@shared-types/common.types';
import type { StatisticDefinition } from '@shared-types/stats.types';

const defaultOrder = Order.DESC;

export const useStatisticSorting = () => {
  const { types } = useStatisticTypes();
  const setSortingAction = useStatsStore((state) => state.setSorting);
  const setOrder = useStatsStore((state) => state.setOrder);

  const defaultSort = useMemo(() => types?.at(0), [types]);

  const [searchParams, setSearchParams] = useSearchParams();
  const querySort = useMemo(
    () => types.find((t) => t.name === searchParams.get('sortBy')) ?? defaultSort,
    [searchParams, defaultSort, types],
  );
  const queryOrder = Order[searchParams.get('order')?.toUpperCase()] ?? defaultOrder;

  const setSorting = useCallback(
    (sortValue: StatisticDefinition, orderValue: Order) => {
      sortValue ??= defaultSort;
      setSortingAction(sortValue);
      orderValue ??= defaultOrder;
      setOrder(orderValue);

      if (
        !sortValue ||
        (sortValue.name === querySort.name && orderValue === queryOrder) ||
        (sortValue.name === defaultSort.name && orderValue === defaultOrder)
      ) {
        return;
      }

      setSearchParams({ sortBy: sortValue.name, order: orderValue.toLowerCase() });
    },
    [defaultSort, setSortingAction, setOrder, querySort?.name, queryOrder, setSearchParams],
  );

  useEffect(() => {
    setSorting(querySort, queryOrder);
  }, [defaultSort, setSorting, querySort, queryOrder, types]);

  const sorting = useStatsStore((state) => state.sorting ?? querySort);
  const order = useStatsStore((state) => state.order ?? queryOrder);

  return { sorting, order, setSorting };
};
